/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import moment from "moment";
import Params from "../common/Params";
import { ApiPromise } from "../models/ApiPromise";
import { axiosCommon } from "../util/axiosSetup";
import { InvoiceResponse } from "../models/invoice/InvoiceResponse";
import { localDateFormat } from "../util/helperFunctions";
import {
  generateInvoiceSearchURL,
  SearchInvoices,
} from "../models/apiparams/SearchInvoices";
import { PageResponse } from "../models/PageResponse";
import { InvoiceRequest } from "../models/invoice/InvoiceRequest";
import { InvoiceStatisticsResponse } from "../models/invoice/InvoiceStatisticsResponse";

export default class ClinicInvoiceApi {
  private static baseUrl = Params.clinicServiceBaseURL;

  static getInvoices(
    clinicId: string,
    request: SearchInvoices
  ): ApiPromise<PageResponse<InvoiceResponse>> {
    const url = generateInvoiceSearchURL(
      `${this.baseUrl}/clinics/${clinicId}/invoices?`,
      request
    );

    return axiosCommon.get(url);
  }

  static createInvoice(
    clinicId: string,
    request: InvoiceRequest
  ): ApiPromise<InvoiceResponse[]> {
    const url = `${this.baseUrl}/clinics/${clinicId}/invoices`;
    return axiosCommon.post(url, request);
  }

  static getPaymentSummary(
    clinicId: string,
    { endDate, startDate }: SearchInvoices
  ): ApiPromise<InvoiceStatisticsResponse> {
    let url = `${this.baseUrl}/clinics/${clinicId}/invoices/payment-summary?`;

    if (startDate !== null) {
      url += `startDate=${moment(startDate)
        .startOf("day")
        .format(localDateFormat())}&`;
    }
    if (endDate !== null) {
      url += `endDate=${moment(endDate)
        .endOf("day")
        .format(localDateFormat())}&`;
    }
    return axiosCommon.get(url);
  }

  static getPaymentSummaryPdf(
    clinicId: string,
    startDate: string | null,
    endDate: string | null
  ): ApiPromise<BlobPart> {
    let url = `${this.baseUrl}/clinics/${clinicId}/invoices/payment-summary/pdf?`;
    if (startDate !== null && startDate.length > 0) {
      url += `startDate=${moment(startDate, localDateFormat()).format(
        localDateFormat()
      )}&`;
    }
    if (endDate !== null && endDate.length > 0) {
      url += `endDate=${moment(endDate, localDateFormat())
        .endOf("day")
        .format(localDateFormat())}&`;
    }
    return axiosCommon.get(url, {
      headers: { Accept: "*/*" },
      responseType: "arraybuffer",
    });
  }
}
